import { APITypesV1 } from "@cur8/api-client";
import {
  Icon16ArrowRight,
  Icon16Checkmark,
  Icon16Copy,
  Typography,
} from "@cur8/maneki";
import { useLocation, useNav } from "@pomle/react-router-paths";
import { usePatientQuery } from "hooks/queries/usePatientQuery";
import { useVisitHistoryQuery } from "hooks/queries/useVisitHistoryQuery";
import { useVisitQuery } from "hooks/queries/useVisitQuery";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useReporting } from "render/hooks/useReporting";
import { paths } from "render/routes/paths";
import { DropdownMenuButton } from "render/ui/trigger/DropdownMenuButton";
import { DropdownItem } from "render/ui/trigger/DropdownMenuButton/components/DropdownItem";
import { PatientDetails } from "./PatientDetails";
import styles from "./styles.module.sass";

export const HeaderRightSideContent = () => {
  const { pathname } = useLocation();

  const params = useMemo(() => {
    return paths.checkIn.parse(pathname);
  }, [pathname]);

  const { data: patient } = usePatientQuery({
    patientId: params?.patientId,
    options: {
      enabled: Boolean(params?.patientId),
    },
  });

  const { data: visitHistory } = useVisitHistoryQuery({
    patientId: params?.patientId,
    options: {
      enabled: Boolean(params?.patientId),
    },
  });

  const { data: visit } = useVisitQuery({
    patientId: params?.patientId,
    visitId: params?.visitId,
  });

  const visitLabel = useMemo(() => {
    if (visit?.medicalExams?.includes(APITypesV1.MedicalExam.NekoBodyScan)) {
      const index =
        visitHistory?.medicalExamVisitTimeSeries.nekoBodyScan?.findIndex(
          (visit) => visit.visitId === params?.visitId
        );
      const length =
        visitHistory?.medicalExamVisitTimeSeries.nekoBodyScan?.length;

      const count = index != null && length != null ? length - index : 1;
      return `Body Scan ${count}`;
    }

    if (visit?.medicalExams && visit.medicalExams.length > 0) {
      return "Follow-up";
    }

    return "Unknown";
  }, [params?.visitId, visit?.medicalExams, visitHistory]);

  const name = patient?.name?.displayName;

  const nav = {
    checkOut: useNav(paths.checkOut),
  };

  return (
    <div className={styles.HeaderRightSideContent}>
      {patient && params ? (
        <>
          <PatientDetails name={name} visitLabel={visitLabel} />
          <DropdownMenuButton>
            {params.patientId != null && params.visitId != null ? (
              <div className={styles.memberDetails}>
                <Typography variant="label-s">{name}</Typography>
                <CopyToClipboardButton textToCopy={params.patientId}>
                  Copy patientID
                </CopyToClipboardButton>
                <CopyToClipboardButton textToCopy={params.visitId}>
                  Copy visitID
                </CopyToClipboardButton>
              </div>
            ) : undefined}
            <DropdownItem
              icon={<Icon16ArrowRight />}
              onClick={() =>
                nav.checkOut.go({
                  patientId: params.patientId,
                  visitId: params.visitId,
                })
              }
            >
              Check out
            </DropdownItem>
          </DropdownMenuButton>
        </>
      ) : undefined}
    </div>
  );
};

function CopyToClipboardButton({
  textToCopy,
  children,
}: React.PropsWithChildren<{ textToCopy: string }>) {
  const { logError } = useReporting();

  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");

  useEffect(() => {
    if (status === "idle") {
      return;
    }

    const timeoutId = setTimeout(() => {
      setStatus("idle");
    }, 2000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [status]);

  const writeToClipboard = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        setStatus("success");
      } catch (error) {
        logError(`Failed to write to clipboard: ${error}`);
        setStatus("idle");
      }
    },
    [logError]
  );

  return (
    <button onClick={() => writeToClipboard(textToCopy)}>
      <Typography variant="body-s">{children}</Typography>
      <div className={styles.copyToClipboardIcon}>
        {status === "success" ? <Icon16Checkmark /> : <Icon16Copy />}
      </div>
    </button>
  );
}
